/**
 * System. Spec 11.4: "Health, migration status, job queue, feature flags, backup status."
 *
 * What is here is what the API can actually answer for. Migration status, backup status and
 * the job queue are not exposed by any procedure — there is no job queue at all (pg-boss is
 * in the stack table and has never been wired up), and backups are spec 15.5's
 * infrastructure concern rather than an application one. Naming them as not-reported beats
 * showing a green tick that means nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminOverview } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/system')({ component: Page });

function Page(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const overview = useAdminOverview();

  /*
   * `/api/ready` rather than a procedure: it is the same endpoint the deployment's health
   * gate uses (spec 18.6), so this screen reports exactly what the load balancer sees.
   */
  const ready = useQuery({
    queryKey: ['admin', 'ready'] as const,
    queryFn: async (): Promise<{ status: string }> => {
      const response = await fetch('/api/ready', { credentials: 'include' });
      return response.json() as Promise<{ status: string }>;
    },
    staleTime: 10_000,
  });

  if (overview.error !== null) return <Alert tone="error">{t(errorKeyFor(overview.error))}</Alert>;

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.system')}</h1>

      <Card title={t('admin:system.health')}>
        {ready.isLoading ? (
          <SkeletonList rows={1} label={t('admin:system.health')} />
        ) : (
          <Badge tone={ready.data?.status === 'ready' ? 'positive' : 'danger'}>
            {ready.data?.status ?? 'unavailable'}
          </Badge>
        )}
      </Card>

      <Card title={t('admin:system.featureFlags')}>
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-ink-muted">{t('admin:overview.jobQueue')}</dt>
            <dd>
              <Badge tone="neutral">
                {overview.data?.jobQueueDepth === null
                  ? t('admin:overview.jobQueueNone')
                  : String(overview.data?.jobQueueDepth ?? '')}
              </Badge>
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
